import { type ReactElement, useCallback } from 'react';
import { Box, Text } from 'components/ui';
import type { DashboardTemplateTileProps } from './DashboardTemplateTile.props';
import {
  TEMPLATE_PREVIEW_CLASS,
  templatePreviewCellSx,
  templatePreviewSx,
  templateTileBadgeSx,
  templateTileDescriptionSx,
  templateTileSx,
  templateTileTitleSx,
} from './DashboardTemplateTile.styles';
import { SUMMARY_DASHBOARD_TEST_IDS } from '../../SummaryDashboard.constants';
import { DASHBOARD_TEMPLATE_TILE_BADGE } from '../DashboardPromptStarter/DashboardPromptStarter.constants';

// One starter template in the empty state's gallery. Clicking it loads the template's prompt into the
// prompt box; it never submits, so the user can read and edit the brief before generating.
const DashboardTemplateTile = ({
  isDisabled = false,
  onApply,
  template,
}: DashboardTemplateTileProps): ReactElement => {
  const { description, id, preview, prompt, title } = template;

  const handleClick = useCallback(() => onApply(prompt), [onApply, prompt]);

  return (
    <Box
      component="button"
      data-testid={SUMMARY_DASHBOARD_TEST_IDS.templateTile}
      // The real attribute rather than aria-disabled: it stops the pointer and Enter/Space alike, so
      // handleClick needs no guard of its own.
      disabled={isDisabled}
      id={`${SUMMARY_DASHBOARD_TEST_IDS.templateTile}-${id}`}
      onClick={handleClick}
      sx={templateTileSx}
      type="button">
      {/* Decoration: the description is what carries the meaning, so the sketch stays out of the
          accessibility tree rather than announcing a run of empty blocks. */}
      <Box
        aria-hidden
        className={TEMPLATE_PREVIEW_CLASS}
        data-testid={SUMMARY_DASHBOARD_TEST_IDS.templateTilePreview}
        sx={templatePreviewSx}>
        {preview.map((cell, index) => (
          // eslint-disable-next-line react/no-array-index-key -- `preview` is a static literal of interchangeable blocks; it never reorders and the cells carry no state
          <Box key={index} sx={templatePreviewCellSx(cell.w)} />
        ))}
      </Box>
      <Box component="span" sx={templateTileBadgeSx}>
        {DASHBOARD_TEMPLATE_TILE_BADGE}
      </Box>
      <Text
        component="p"
        data-testid={SUMMARY_DASHBOARD_TEST_IDS.templateTileTitle}
        sx={templateTileTitleSx}>
        {title}
      </Text>
      <Text
        component="p"
        data-testid={SUMMARY_DASHBOARD_TEST_IDS.templateTileDescription}
        sx={templateTileDescriptionSx}>
        {description}
      </Text>
    </Box>
  );
};

export default DashboardTemplateTile;
